import React, { useState, useEffect } from "react";

/** Nextjs */
import dynamic from "next/dynamic";

/** MUI */
import { makeStyles } from "@mui/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Container, Grid, Typography } from "@mui/material";

/** Redux */
import { useDispatch, useSelector } from "react-redux";
import { getIs1920, setSkipLinksPage } from "../../_appStore/Common";

/** API */
import { getFAQFromAPI } from "../../_APIs";

/** Helpers */
import { useTranslation } from "react-i18next";
import noApiFAQs from "./noApiFAQs.json";

/** matomo */
import { useMatomo } from "@datapunt/matomo-tracker-react";

/** UI */
// const HelpAccordion = dynamic(() => import("./components/HelpAccordion"));
import HelpAccordion from "./components/HelpAccordion";
const Preloader = dynamic(() => import("../../_common/Preloader"));

const useStyles = makeStyles((theme) => ({
    pageTitle: {
        fontSize: "1.75rem",
        fontWeight: 500,
        width: "50%",
        margin: "0 auto 32px auto",
        textAlign: "center",
        borderBottom: "1px solid #CCC",
    },
    titles: {
        fontSize: "1.77778rem",
        fontWeight: 600,
        marginBottom: 14, //theme.spacing(4),
        color: theme.palette.primary.main,
        "&:focus-visible": {
            ...theme.outliness,
        },
        [theme.breakpoints.down("xl")]: {
            fontSize: "1.33333334rem",
        },
        [theme.breakpoints.down("lg")]: {
            fontSize: "1.11111111rem",
        },
    },
}));

const PomocIndex = (props) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { trackPageView } = useMatomo();

    const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));
    const downXL = useMediaQuery((theme) => theme.breakpoints.down("xl"));

    /** States */
    const [LOADING, setLOADING] = useState(false);
    const [ERROR, setERROR] = useState(false);
    const [FAQS, setFAQS] = useState(noApiFAQs);
    /** - redux */
    const is1920 = useSelector(getIs1920);

    /** Effects */
    useEffect(() => {
        // trackPageView();
        dispatch(setSkipLinksPage({ id: "help" }));
        return () => dispatch(setSkipLinksPage(""));
    }, []);

    useEffect(() => {
        setLOADING(true);
        setERROR(false);
        getFAQFromAPI()
            .then((response) => {
                if (response.error || response.message?.type === "Error") {
                    setERROR(response.message);
                } else {
                    setFAQS(response);
                }
            })
            .catch((error) => {
                console.log(error);
            })
            .finally(() => {
                setLOADING(false);
            });
    }, []);

    return (
        <Container maxWidth={"xl"} sx={{ pb: "15px", pt: "7px", px: { xs: 2, sm: 2, md: 2 } }} disableGutters>
            {!!LOADING && <Preloader type={"linear"} />}

            <Grid container spacing={downXL ? 0 : 19}>
                <Grid item xs={12} md={6} style={{ paddingRight: is1920 ? 11 : isMobile ? 0 : 32 }}>
                    <Grid container spacing={0}>
                        <Grid item xs={12}>
                            <Typography
                                className={classes.titles}
                                id={"help-guide-title"}
                                tabIndex={-1}
                                component={"h2"}
                            >
                                {t("Wskazówki dla Użytkownika")}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            {!!FAQS?.db_notes?.length && (
                                <HelpAccordion
                                    items={FAQS.db_notes}
                                    type={"OTHER"}
                                    tip={{ open: t("Rozwiń wskazówkę"), close: t("Zwiń wskazówkę") }}
                                    label={{
                                        open: t("Rozwiń wskazówkę, aby zobaczyć jej pełną treść"),
                                        close: t("Zwiń wskazówkę"),
                                    }}
                                    // type={"DB"}
                                />
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            {!!FAQS?.areas_notes?.length && (
                                <HelpAccordion
                                    items={FAQS.areas_notes}
                                    type={"OTHER"}
                                    tip={{ open: t("Rozwiń wskazówkę"), close: t("Zwiń wskazówkę") }}
                                    label={{
                                        open: t("Rozwiń wskazówkę, aby zobaczyć jej pełną treść"),
                                        close: t("Zwiń wskazówkę"),
                                    }}
                                    // type={"AREA"}
                                />
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            {!!FAQS?.other_notes?.length && <HelpAccordion items={FAQS.other_notes} type={"OTHER"} />}
                        </Grid>
                    </Grid>
                </Grid>
                <Grid
                    item
                    xs={12}
                    md={6}
                    style={{ paddingLeft: is1920 ? 143 : "unset", paddingRight: is1920 ? 0 : "unset" }}
                >
                    <Grid container spacing={0}>
                        <Grid item xs={12}>
                            <Typography
                                className={classes.titles}
                                sx={{ mt: isMobile ? 3 : 0 }}
                                id={"help-faq-title"}
                                tabIndex={-1}
                                component={"h2"}
                            >
                                {t("FAQ")}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            {!!FAQS?.db_faq?.length && (
                                <HelpAccordion
                                    items={FAQS.db_faq}
                                    type={"OTHER"}
                                    tip={{ open: t("Rozwiń pytanie"), close: t("Zwiń pytanie") }}
                                    label={{
                                        open: t("Rozwiń pytanie, aby zobaczyć odpowiedź"),
                                        close: t("Zwiń pytanie"),
                                    }}
                                />
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            {!!FAQS?.area_faq?.length && (
                                <HelpAccordion
                                    items={FAQS.area_faq}
                                    type={"OTHER"}
                                    tip={{ open: t("Rozwiń pytanie"), close: t("Zwiń pytanie") }}
                                    label={{
                                        open: t("Rozwiń pytanie, aby zobaczyć odpowiedź"),
                                        close: t("Zwiń pytanie"),
                                    }}
                                />
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            {!!FAQS?.other_faq?.length && <HelpAccordion items={FAQS.other_faq} type={"OTHER"} />}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </Container>
    );
};

export default PomocIndex;
